import { useCallback, useEffect, useRef, useState } from "react";
import { INDIAN_STATES } from "@gatiq/shared";
import { SelectField, TextInput } from "@/ui";
import { api } from "@/lib/api";
import { PostalPlaceSelect } from "./PostalPlaceSelect";
import { postalAddressPatch, type PostalSource } from "@/lib/postalAddress";
import { useLanguage } from "@/i18n/language";

/**
 * Where a place of business sits — shared by the depot form and the counter
 * form, because they are the same kind of thing to anyone doing the paperwork.
 * Two forms asking for an address in two different ways is how the same shop
 * ends up recorded twice.
 *
 * DISTRICT and PIN CODE are filled from India Post's free directory once the
 * state and town are known: they are facts about the place, not decisions. Both
 * stay editable — a lookup that guesses wrong must never be able to stop
 * someone saving, and the form works with the dataset unreachable.
 */

export interface AddressFormState {
  state: string;
  city: string;
  district: string;
  pincode: string;
  village: string;
  address: string;
  gstNumber: string;
}

export const EMPTY_ADDRESS: AddressFormState = {
  state: "",
  city: "",
  district: "",
  pincode: "",
  village: "",
  address: "",
  gstNumber: "",
};

interface GeoMatch {
  locality: string;
  district: string;
  state: string;
  pincode: string;
}

const STATE_OPTIONS = INDIAN_STATES.map((s) => ({ value: s, label: s }));

export function AddressFields({
  value,
  errors,
  onChange,
  required = false,
  showGst = true,
  endpoint = "/public/geo/places",
  language = "en",
  addressRequired = false,
  pincodeRequired = false,
}: {
  value: AddressFormState;
  errors: Record<string, string>;
  onChange: (patch: Partial<AddressFormState>) => void;
  /** Depots must name a state and town; counters may be added without one. */
  required?: boolean;
  showGst?: boolean;
  endpoint?: string;
  language?: "en" | "hi";
  addressRequired?: boolean;
  pincodeRequired?: boolean;
}) {
  const { t } = useLanguage(language);
  const [matches, setMatches] = useState<GeoMatch[]>([]);
  const [looking, setLooking] = useState(false);
  const [looked, setLooked] = useState(false);
  const [lookup, setLookup] = useState<{ source: PostalSource; query: string } | null>(null);

  /*
   * Held in a ref so the debounce effect can fill fields without listing
   * `onChange` among its dependencies — the parent rebuilds that closure on
   * every keystroke, which would restart the timer forever and never fire.
   */
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;

  const { state } = value;

  const apply = useCallback((m: GeoMatch) => {
    onChangeRef.current({ ...postalAddressPatch([m], lookup?.source ?? "city", valueRef.current), village: m.locality });
  }, [lookup?.source]);

  useEffect(() => {
    const term = lookup?.query.trim() ?? "";
    setMatches([]);
    if (lookup?.source === "pincode" ? !/^[1-9]\d{5}$/.test(term) : term.length < 3) {
      setMatches([]);
      setLooked(false);
      setLooking(false);
      return;
    }

    let live = true;
    setLooking(true);
    const timer = setTimeout(() => {
      const params = new URLSearchParams({ q: term });
      if (state && lookup?.source !== "pincode") params.set("state", state);
      api
        .get<{ matches: GeoMatch[] }>(`${endpoint}?${params.toString()}`)
        .then((res) => {
          if (!live) return;
          setMatches(res.matches);
          setLooked(true);
          onChangeRef.current(postalAddressPatch(res.matches, lookup?.source ?? "city", valueRef.current));
        })
        .catch(() => {
          if (live) setMatches([]);
        })
        .finally(() => {
          if (live) setLooking(false);
        });
    }, 450);

    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [lookup, lookup?.source === "pincode" ? "" : state, endpoint]);

  const set =
    (key: keyof AddressFormState) =>
    (e: { target: { value: string } }): void => {
      const next = key === "pincode" ? e.target.value.replace(/\D/g, "").slice(0, 6) : e.target.value;
      onChange({ [key]: next } as Partial<AddressFormState>);
      if (key === "city" || key === "pincode" || key === "village") setLookup({ source: key, query: next });
    };

  const cityHint = looking
    ? "Looking up district and PIN…"
    : looked && matches.length === 0
      ? "No match — fill district and PIN yourself."
      : undefined;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SelectField
          error={errors.state}
          label={t("राज्य", "State")}
          onChange={set("state")}
          options={STATE_OPTIONS}
          placeholder="Choose a state"
          required={required}
          value={value.state}
        />
        <TextInput
          error={errors.city}
          hint={cityHint}
          label={t("शहर / कस्बा", "City / town")}
          onChange={set("city")}
          placeholder="e.g. Hanumangarh"
          required={required}
          value={value.city}
        />
      </div>

      {matches.length > 0 ? <PostalPlaceSelect matches={matches} value={value} onSelect={apply} /> : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextInput
          error={errors.district}
          hint="From the PIN directory"
          label={t("जिला", "District")}
          onChange={set("district")}
          placeholder="Auto-filled"
          value={value.district}
        />
        <TextInput
          error={errors.pincode}
          hint="From the PIN directory"
          inputMode="numeric"
          maxLength={6}
          label={t("पिन कोड", "PIN code")}
          onChange={set("pincode")}
          placeholder="Auto-filled"
          required={pincodeRequired}
          value={value.pincode}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextInput
          error={errors.village}
          hint="Optional"
          label={t("गाँव", "Village")}
          onChange={set("village")}
          placeholder="e.g. Sangaria"
          value={value.village}
        />
        {showGst && <TextInput
          error={errors.gstNumber}
          hint="Optional"
          label="GST number"
          onChange={(e) => onChange({ gstNumber: e.target.value.toUpperCase() })}
          placeholder="e.g. 08AAACV1234A1Z5"
          value={value.gstNumber}
        />}
      </div>

      <TextInput
        error={errors.address}
        hint={addressRequired ? "Street, building, landmark" : "Optional — street, building, landmark"}
        required={addressRequired}
        label={t("पता", "Address")}
        onChange={set("address")}
        placeholder="e.g. Plot 42, Sitapura Industrial Area"
        value={value.address}
      />
    </div>
  );
}

/**
 * A titled block of fields.
 *
 * The depot form grew from four fields to eleven and became a wall of inputs
 * with no shape. Grouping them means someone filling it in scans three short
 * lists rather than one long one, and the optional half is visibly optional.
 */
export function FormSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5 border-b border-rule/70 pb-2">
        <h3 className="font-title text-[13px] font-semibold tracking-[0.02em] text-ink uppercase">
          {title}
        </h3>
        {description ? <p className="text-xs text-muted">{description}</p> : null}
      </div>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
}
